/**
 * Dynamic type validation inspired by objectmodel.

 * IMPORTANT: We do not use objectmodel because Version 4 cannot be polyfiled
 * in IE11 and the IE11-compatible version 2 has bugs.
 *
 * @see https://objectmodel.js.org/docs/v2/ (v2 doc)
 * @see https://objectmodel.js.org
 */
import {
    __, allPass, anyPass, append, both, complement, compose, concat, cond,
    curry, either, endsWith, equals, ifElse, isNil, join, keys, map, reduce,
    propOr, startsWith, T, unless, when,
} from 'ramda'
import { isArray as RAisArray, isObject } from 'ramda-adjunct'


/**
 * For same strange reason, we cannot use the ramda version of `is`
 * because it fails on the server side
 *
 * @sig (* -> {*}) -> a -> Boolean
 * @see https://ramdajs.com/docs/#is
 */
const is = curry((Ctor, val) => val != null && val.constructor === Ctor || val instanceof Ctor) // eslint-disable-line

/**
 * For some strange reason, is(Array, []), fails on the server
 * when using during property validation :/
 *
 * The isArray version of `ramda-adjunct` works.
 *
 * @see https://char0n.github.io/ramda-adjunct/2.20.0/RA.html#.isArray
 */
const isArray = RAisArray

/**
 * Nasty workaround for IE11. For some reason, in IE11 it does not work to use:
 *
 * const obj = { a : Number }
 * equals(Number, obj.a)
 *
 * @sig a -> Boolean
 */
const equalsNumber = either(x => x === Number, equals(Number))

/* eslint-disable no-use-before-define */
// we are using circular dependent functions

/**
 * Type used for validating an Array with fixed type.
 */
function ArrayModel (spec) {
    this.spec = spec

    this.toJSON = () => `ArrayOf(${modelToString(spec)})`
}

/**
 * Type used for validating that the input validates one of the given
 * schemata/types.
 */
function OneOfModel (...specs) {
    this.specs = specs

    this.toJSON = () => `OneOf(${join(', ', map(modelToString, specs))})`
}

/**
 * Type used for validating optional properties.
 */
function OptionalModel (spec) {
    this.spec = spec

    this.toJSON = () => `Optional(${modelToString(spec)})`
}

/**
 * Type used for object schema validation.
 */
function ObjectModel (spec, validators = []) {
    this.spec = spec
    this.validators = validators

    this.toJSON = () => `ObjectModel(${modelToString(spec)})`
}

/**
 * Schema type for validating all entries of an array to match the schema.
 *
 * @sig Schema -> ArrayModel
 * @example
 *
 * const model = {
 *     b: String,
 *     c: ArrayOf({
 *          a: ArrayOf(String),
 *          d: OneOf(String, Number, Boolean),
 *     }),
 * }
 */
export const ArrayOf = spec => new ArrayModel(spec)

/**
 * Schema type for validating one of multiple schemata.
 *
 * @sig (schema_1, ..., schema_n) -> OneOfModel
 * @example
 *
 * const model = {
 *     a: OneOf(Boolean, Number, '1', '2'),
 *     b: ArrayOf({
 *         bla: Number,
 *         c: OneOf(String, Number),
 *     }),
 * }
 */
export const OneOf = (...specs) => new OneOfModel(...specs)

/**
 * Schema type for validating an optional property.
 *
 * @sig schema -> OptionalModel
 * @example
 *
 * const model = {
 *     a: Optional(String),
 *     b: ArrayOf({
 *         c: Optional(ArrayOf(String)),
 *     }),
 * }
 */
export const Optional = spec => new OptionalModel(spec)

/**
 * Construct a model with optional assertions.
 *
 * @typedef (* -> Boolean) Pred
 * @sig (Schema, [[Pred, String]] -> ObjectModel
 * @example
 *
 * const model = toObjectModel({
 *     a: String,
 *     b: Number,
 * }, [
 *     [obj => obj.a === 'foo', 'Property a must be foo'],
 *     [obj => 1 < obj.b < 10, 'Property b must between 1 and 10'],
 * ])
 */
export const toObjectModel = (spec, validators = []) => new ObjectModel(spec, validators)

// joinErrorPath :: [String] -> String
const joinErrorPath = reduce(
    (str, propName) => ifElse(
        both(startsWith('['), endsWith(']')),
        concat(str),
        concat(`${str}${str ? '.' : ''}`),
    )(propName),
    '',
)

// formatErrors :: [{ errorPath: [String], msg: String }] -> [String]
export const formatErrors = map(({ errorPath, msg }) => `At "${joinErrorPath(errorPath)}": ${msg}`)

// isPureObject :: * -> Boolean
const isPureObject = allPass([
    isObject,
    complement(isArray),
    complement(is(Function)),
])

// addError :: (a -> Boolean) -> a -> [String] -> [Error] -> [Error]
const addError = curry((isValid, data, msg, errorPath, errors) => unless(
    () => isValid(data),
    append({ errorPath, msg }),
)(errors))

// modelToString :: * -> String
const modelToString = cond([
    [equals(String), () => 'String'],
    [equals(RegExp), () => 'RegExp'],
    [equalsNumber, () => 'Number'],
    [equals(Array), () => 'Array'],
    [equals(Boolean), () => 'Boolean'],
    [equals(Object), () => 'Object'],
    [equals(Function), () => 'Function'],
    [is(ArrayModel), spec => `ArrayOf(${modelToString(spec.spec)})`],
    [is(OptionalModel), spec => `Optional(${modelToString(spec.spec)})`],
    [is(OneOfModel), spec => `OneOf(${join(', ', map(modelToString, spec.specs))})`],
    [isPureObject, x => JSON.stringify(x)],
    [is(String), x => x],
    [T, toString],
])

/**
 * Validate the shema of an ObjectModel.
 *
 * @sig ObjectModel -> a -> [String] -> [Error] -> [Error]
 */
const validateObjectModel = curry((spec, data, errorPath, errors) => compose(
    when(
        a => a.length === errors.length,
        reduce(
            (newErrors, [pred, msg]) => addError(pred, data, msg, errorPath, newErrors),
            __,
            spec.validators,
        ),
    ),
    reduce(
        (newErrors, key) => validate(spec.spec[key], propOr(undefined, key, data), append(key, errorPath), newErrors),
        __,
        keys(spec.spec),
    ),
)(errors))

/**
 * Validate the shema of an ArrayModel.
 *
 * @sig ArrayModel -> a -> [String] -> [Error] -> [Error]
 */
const validateArrayModel = curry((spec, data, errorPath, errors) => ifElse(
    () => isArray(data),
    err => data.reduce(
        (currentErrors, val, index) => validate(spec.spec, val, append(`[${index}]`, errorPath), currentErrors),
        err,
    ),
    addError(isArray, data, 'Not an array', errorPath),
)(errors))

/**
 * Validate the shema of a OneOfModel.
 *
 * @sig OneOfModel -> a -> [String] -> [Error] -> [Error]
 */
const validateOneOfModel = curry((specs, data, errorPath, errors) => addError(
    anyPass(map(validateIfIsModel(false), specs.specs)),
    data,
    `Not one of '${join('\', \'', specs.specs.map(modelToString))}"`,
    errorPath,
    errors,
))

/**
 * Validate the shema of an OptionalModel.
 *
 * @sig OptionalModel -> a -> [String] -> [Error] -> [Error]
 */
const validateOptionalModel = curry((spec, data, errorPath, errors) => unless(
    () => isNil(data),
    validate(spec.spec, data, errorPath),
)(errors))

/**
 * Validate the schema of the input ('data') and append possible errors
 * to the input errors.
 *
 * @sig Schema -> a -> [String] -> [Error] -> [Error]
 */
const validate = curry((spec, data, errorPath, errors) => cond([
    [
        () => is(ArrayModel, spec),
        validateArrayModel(spec, data, errorPath),
    ],
    [
        () => is(ObjectModel, spec),
        validateObjectModel(spec, data, errorPath),
    ],
    [
        () => is(OneOfModel, spec),
        validateOneOfModel(spec, data, errorPath),
    ],
    [
        () => is(OptionalModel, spec),
        validateOptionalModel(spec, data, errorPath),
    ],
    [
        () => isArray(spec),
        e => validateOneOfModel(OneOf(...spec), data, errorPath, e),
    ],
    [
        () => isPureObject(spec),
        validateObjectModel(toObjectModel(spec), data, errorPath),
    ],
    [() => equalsNumber(spec), addError(is(Number), data, 'Not a Number', errorPath)],
    [() => equals(String, spec), addError(is(String), data, 'Not a String', errorPath)],
    [() => equals(RegExp, spec), addError(is(RegExp), data, 'Not a RegExp', errorPath)],
    [() => equals(Object, spec), addError(isObject, data, 'Not a Object', errorPath)],
    [() => equals(Array, spec), addError(isArray, data, 'Not an Array', errorPath)],
    [() => equals(Boolean, spec), addError(is(Boolean), data, 'Not a Boolean', errorPath)],
    [() => equals(Function, spec), addError(is(Function), data, 'Not a Boolean', errorPath)],
    [() => isNil(spec), addError(isNil, data, 'Has to be null|undefined', errorPath)],
    [T, addError(equals(spec), data, `Has to equal to ${spec}`, errorPath)],
    [T, () => {
        throw new Error(`Invalid spec. No way to handle "${spec}".`)
    }],
])(errors))

/**
 * Validate that the input data matches the input schema and log the errors if
 * logging is enabled.
 *
 * @sig Boolean -> Schema -> a -> Boolean
 * @example
 *
 * const validate = validateIfIsModel(false, {
 *     k: ArrayOf({ a: String, b: Number }),
 *     b: { nested: Number },
 * })
 * validate({}) // false
 * validate({ b: { nested: 1 } }) // fase
 * validate({
 *     b: { nested: 1 },
 *     k: [
 *         { a: 'a', b: 4 },
 *         { a: 'foo', b: 42 },
 *     ]
 * }) // true
 * validate({
 *     b: { nested: 1 },
 *     k: [{ a: 'a', b: 'not a number' }]
 * }) // false
 */
export const validateIfIsModel = curry((enableLogging, spec, obj) => {
    const errs = validate(spec, obj, [], [])

    if (enableLogging && errs.length && process.env.TEST !== 'unit') {
        console.log(formatErrors(errs))
    }

    return errs.length === 0
})

// isModelNoLog :: Schema -> a -> Boolean
export const isModelNoLog = validateIfIsModel(false)

// isModel :: Schema -> a -> Boolean
export const isModel = validateIfIsModel(true)

/**
 * Validate the input to match the schema and throw Error if it does not comply.
 *
 * @sig ObjectModel Spec -> Obj -> Obj | Error
 * @see https://objectmodel.js.org/#doc-basic-model
 */
export const throwIfNotModel = curry((spec, obj) => {
    const errs = validate(spec, obj, [], [])

    if (errs.length) {
        throw formatErrors(errs)
    }

    return obj
})

/**
 * Reject Promise with error message if input does not comply to schema. Otherwise
 * return input (unchanged).
 *
 * @sig ObjectModel Spec -> Obj -> Promise Obj Error
 */
export const rejectIfNotModel = curry((spec, data) => {
    try {
        return throwIfNotModel(spec, data)
    } catch (e) {
        return Promise.reject(e)
    }
})
